import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import api, { clearSessionToken } from "@/lib/api";

const AuthContext = createContext(null);

const DEFAULT_SETTINGS = {
  portal_name: "Griffin Consultancy Pulse",
  portal_tagline: "Internal Portal",
  logo_file_id: null,
};

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);

  const loadSettings = useCallback(async () => {
    try {
      const r = await api.get("/settings");
      setSettings({ ...DEFAULT_SETTINGS, ...(r.data || {}) });
    } catch (e) {
      console.error("settings load failed, using defaults:", e);
      setSettings(DEFAULT_SETTINGS);
    }
  }, []);

  const checkAuth = useCallback(async () => {
    try {
      const r = await api.get("/auth/me");
      setUser(r.data);
      await loadSettings();
    } catch (e) {
      // Expected when not logged in — surface only at debug level
      console.debug("auth/me unauthenticated:", e?.response?.status);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, [loadSettings]);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (e) {
      console.error("auth/logout failed (ignoring):", e);
    }
    clearSessionToken();
    setUser(null);
    window.location.href = "/login";
  };

  const value = useMemo(
    () => ({ user, setUser, loading, checkAuth, logout, settings, refreshSettings: loadSettings }),
    [user, loading, checkAuth, settings, loadSettings],
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
